import Button from '@/components/Buttons/Button';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import Switch from '@/components/Switch/Switch';
import { DeviceSelect } from '@/components/Teleprompter/Controls/DeviceSelect';
import CustomTooltip from '@/components/ToolTip/CustomTooltip';
import Microphone from '@/images/microphone1.svg';
import Record from '@/images/Record.svg';
import Videocamera from '@/images/Videocamera.svg';
import { AvatarType } from '@/pages/avatar/common';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import { BlackInformationChip, InformationChipWrapper } from '../BlackInformationChip';

const Container = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
`;

const StyledSwitch = styled(Switch)`
    .MuiSwitch-thumb {
        color: ${({ theme }) => theme.colors.secondary.main};
    }

    .MuiSwitch-track {
        background-color: ${({ theme }) => theme.colors.black[100]};
    }

    .MuiSwitch-switchBase {
        &.Mui-checked {
            + .MuiSwitch-track {
                background-color: ${({ theme }) => theme.colors.warn[400]};
            }
        }
    }
`;

const DevicesSelect = styled.div`
    display: flex;
    margin-top: auto;
    margin-bottom: 32px;
    gap: 24px;
`;

const PreviewInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    max-width: 164px;

    color: ${({ theme }) => theme.colors.success[550]};
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.4px;
`;

const Footer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    max-width: 500px;
`;

const StyledTooltip = styled(CustomTooltip)`
    width: 100%;
`;

const StyledButton = styled(Button)`
    gap: 4px;
    width: 100%;
`;

const StyledAgentsText = styled(Typography)`
    color: ${({ theme }) => theme.colors.black[600]};
    font-size: 12px;
    font-weight: 400;
`;

const SafeZoneHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const SafeZone = styled.div`
    position: relative;
    padding: 16px;
    border-radius: 8px;
    gap: 8px;
    background-color: ${({ theme }) => theme.colors.secondary.main};
    color: ${({ theme }) => theme.colors.white};
    display: flex;
    flex-direction: column;
`;

interface Props {
    selectedVideoDevice: string;
    selectedAudioDevice: string;
    videoDevices: MediaDeviceInfo[];
    audioDevices: MediaDeviceInfo[];
    setSelectedVideoDevice: (deviceId: string) => void;
    setSelectedAudioDevice: (deviceId: string) => void;
    handleTriggerRecording: () => void;
    safeZoneToggle: boolean;
    setSafeZoneToggle: (toggle: boolean) => void;
    avatarType?: AvatarType;
}

export const RecordingSetup = ({
    selectedVideoDevice,
    selectedAudioDevice,
    videoDevices,
    audioDevices,
    setSelectedVideoDevice,
    setSelectedAudioDevice,
    handleTriggerRecording,
    safeZoneToggle,
    avatarType,
    setSafeZoneToggle,
}: Props) => {
    const theme = useTheme();

    const handleSwitchChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setSafeZoneToggle(checked);
    };

    const dvpInformationChip = (
        <InformationChipWrapper>
            <DeviceSelect
                showLabel={false}
                options={videoDevices}
                value={selectedVideoDevice}
                onChange={setSelectedVideoDevice}
                icon={Videocamera}
                iconColor={theme.colors.white}
                lightTheme={true}
            />
            <DeviceSelect
                showLabel={false}
                options={audioDevices}
                value={selectedAudioDevice}
                onChange={setSelectedAudioDevice}
                icon={Microphone}
                iconColor={theme.colors.white}
                lightTheme={true}
            />
            <PreviewInfo>
                <MaskedIcon src={Record} size="32px" color={theme.colors.success[550]} />
                Preview
            </PreviewInfo>
        </InformationChipWrapper>
    );

    const blackInformationChip = (
        <>
            <BlackInformationChip>
                <PreviewInfo>
                    <MaskedIcon src={Record} size="32px" color={theme.colors.success[550]} />
                    Preview
                </PreviewInfo>
            </BlackInformationChip>

            <DevicesSelect>
                <DeviceSelect
                    options={videoDevices}
                    value={selectedVideoDevice}
                    onChange={setSelectedVideoDevice}
                    icon={Videocamera}
                    iconColor={theme.colors.secondary.main}
                />

                <DeviceSelect
                    options={audioDevices}
                    value={selectedAudioDevice}
                    onChange={setSelectedAudioDevice}
                    icon={Microphone}
                    iconColor={theme.colors.secondary.main}
                />
            </DevicesSelect>
        </>
    );

    return (
        <Container>
            {avatarType === AvatarType.PremiumPlus ? dvpInformationChip : blackInformationChip}

            <Footer>
                <StyledTooltip
                    title="You need to select recording device"
                    disableHoverListener={!!selectedVideoDevice && !!selectedAudioDevice}>
                    <StyledButton
                        color="secondary"
                        onClick={handleTriggerRecording}
                        disabled={!selectedVideoDevice || !selectedAudioDevice}>
                        <MaskedIcon src={Record} size="24px" color={theme.colors.error[600]} />
                        Start recording
                    </StyledButton>
                </StyledTooltip>
                {avatarType === AvatarType.PremiumPlus && (
                    <SafeZone>
                        <SafeZoneHeader>
                            <Typography variant="h5">Agents safe zone</Typography>
                            <StyledSwitch checked={safeZoneToggle} onChange={handleSwitchChange} />
                        </SafeZoneHeader>
                        <StyledAgentsText>
                            When recording for agent use the safe zone to make sure <br />
                            your face positoin within the guides and fully visible
                        </StyledAgentsText>
                    </SafeZone>
                )}
            </Footer>
        </Container>
    );
};
